import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { MaterialPeca } from '../componentes/Peca'

type Ponto = [number, number, number]

/** Fixações e nervuras agrupadas em uma malha para cada peça selecionável. */
export function MalhaDetalhe({ id, criar, cor, nome }: {
  id: string; criar: () => THREE.BufferGeometry; cor?: string; nome: string
}) {
  const geometria = useMemo(criar, [criar])
  useEffect(() => () => geometria.dispose(), [geometria])
  return <mesh name={nome} geometry={geometria}><MaterialPeca id={id} corBase={cor} metalico={0.55} rugosidade={0.45} /></mesh>
}

export function unirDetalhes(pecas: THREE.BufferGeometry[]) {
  const unidas = mergeGeometries(pecas)!
  pecas.forEach(peca => peca.dispose())
  return unidas
}

export function parafuso(posicao: Ponto, raio = 0.012) {
  return new THREE.CylinderGeometry(raio, raio, raio * 0.65, 6).translate(...posicao)
}

export function TampaMotorDetalhada({ largura, profundidade }: { largura: number; profundidade: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    // Tampa de válvulas com nervuras longitudinais, fixações e bocal de óleo.
    for (let i = 0; i < 5; i++) pecas.push(new THREE.BoxGeometry(largura * 0.78, 0.014, 0.013)
      .translate(0, 0.075, (i - 2) * profundidade * 0.14))
    for (const x of [-1, 1]) for (const z of [-1, 1]) {
      pecas.push(parafuso([x * largura * 0.4, 0.082, z * profundidade * 0.37]))
    }
    pecas.push(new THREE.CylinderGeometry(0.032, 0.038, 0.035, 16).translate(largura * 0.23, 0.088, 0))
    return unirDetalhes(pecas)
  }, [largura, profundidade])
  return <MalhaDetalhe id="motor.cabecote" nome="tampa-valvulas-fixacoes" criar={criar} />
}

export function BlocoMotorDetalhado({ largura, profundidade }: { largura: number; profundidade: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    for (const face of [-1, 1]) {
      for (let i = 0; i < 6; i++) pecas.push(new THREE.BoxGeometry(0.014, 0.24, 0.02)
        .translate((i - 2.5) * largura * 0.145, -0.015, face * (profundidade / 2 + 0.006)))
      for (let i = 0; i < 3; i++) pecas.push(new THREE.CylinderGeometry(0.027, 0.027, 0.012, 16)
        .rotateX(Math.PI / 2).translate((i - 1) * largura * 0.27, 0.03, face * (profundidade / 2 + 0.015)))
    }
    return unirDetalhes(pecas)
  }, [largura, profundidade])
  return <MalhaDetalhe id="motor.bloco" nome="bloco-fundido-nervuras" criar={criar} />
}

export function ColetoresAdmissaoDetalhados({ largura, profundidade }: { largura: number; profundidade: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    // Quatro dutos: um exemplo visual, sem atribuir cilindrada ao perfil.
    for (let i = 0; i < 4; i++) {
      const x = (i - 1.5) * largura * 0.2
      const curva = new THREE.CatmullRomCurve3([
        new THREE.Vector3(x, 0.03, 0), new THREE.Vector3(x, 0.12, 0.02),
        new THREE.Vector3(x, 0.16, profundidade * 0.3), new THREE.Vector3(x, 0.03, profundidade * 0.43),
      ])
      pecas.push(new THREE.TubeGeometry(curva, 12, 0.024, 8, false))
      pecas.push(parafuso([x, 0.082, -0.04], 0.009))
    }
    return unirDetalhes(pecas)
  }, [largura, profundidade])
  return <MalhaDetalhe id="motor.coletorAdmissao" nome="dutos-curvos-admissao" criar={criar} cor="#6a727c" />
}

export function CarcacaCambioDetalhada({ largura, altura, profundidade }: { largura: number; altura: number; profundidade: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    for (const lado of [-1, 1]) for (let i = 0; i < 6; i++) {
      pecas.push(new THREE.BoxGeometry(0.016, altura * 0.8, 0.018)
        .translate((i - 2.5) * largura * 0.15, 0, lado * (profundidade / 2 + 0.006)))
    }
    for (let i = 0; i < 8; i++) {
      const angulo = i * Math.PI / 4
      pecas.push(parafuso([Math.cos(angulo) * largura * 0.44, altura / 2 + 0.008, Math.sin(angulo) * profundidade * 0.44], 0.01))
    }
    return unirDetalhes(pecas)
  }, [largura, altura, profundidade])
  return <MalhaDetalhe id="cambio" nome="carcaca-cambio-nervuras" criar={criar} />
}

export function PainelEstampadoDetalhado({ id, largura, altura }: { id: string; largura: number; altura: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    for (const face of [-1, 1]) for (let i = 0; i < 7; i++) {
      const x = (i - 3) * largura * 0.12
      pecas.push(new THREE.BoxGeometry(0.024, altura * 0.62, 0.016).translate(x, 0, face * 0.024))
    }
    for (const y of [-1, 1]) pecas.push(new THREE.BoxGeometry(largura * 0.92, 0.015, 0.055).translate(0, y * altura * 0.45, 0))
    return unirDetalhes(pecas)
  }, [largura, altura])
  return <MalhaDetalhe id={id} nome="nervuras-painel-estampado" criar={criar} />
}

export function FixacoesTorreDetalhadas({ id }: { id: string }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = [new THREE.CylinderGeometry(0.035, 0.045, 0.027, 16)]
    for (let i = 0; i < 3; i++) {
      const a = i * Math.PI * 2 / 3
      pecas.push(parafuso([Math.cos(a) * 0.079, 0, Math.sin(a) * 0.079]))
      pecas.push(new THREE.TorusGeometry(0.017, 0.003, 6, 12).rotateX(Math.PI / 2)
        .translate(Math.cos(a) * 0.079, -0.005, Math.sin(a) * 0.079))
    }
    return unirDetalhes(pecas)
  }, [])
  return <MalhaDetalhe id={id} nome="fixacoes-torre-amortecedor" criar={criar} />
}

export function AletasRadiadorDetalhadas({ id, largura, altura }: { id: string; largura: number; altura: number }) {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = []
    for (let i = 0; i < 22; i++) {
      pecas.push(new THREE.BoxGeometry(largura * 0.94, 0.004, 0.012).translate(0, (i / 21 - 0.5) * altura * 0.92, 0))
    }
    for (const x of [-1, 1]) pecas.push(new THREE.BoxGeometry(0.04, altura + 0.02, 0.065).translate(x * largura / 2, 0, 0))
    return unirDetalhes(pecas)
  }, [largura, altura])
  return <MalhaDetalhe id={id} nome="aletas-trocador-calor" criar={criar} />
}

export function VentoinhaDetalhada() {
  const criar = useMemo(() => () => {
    const pecas: THREE.BufferGeometry[] = [new THREE.TorusGeometry(0.16, 0.009, 8, 32),
      new THREE.CylinderGeometry(0.034, 0.034, 0.045, 16).rotateX(Math.PI / 2)]
    for (let i = 0; i < 7; i++) {
      const a = i * Math.PI * 2 / 7
      pecas.push(new THREE.BoxGeometry(0.045, 0.11, 0.008).rotateY(0.35).translate(0.018, 0.085, 0).rotateZ(a))
    }
    return unirDetalhes(pecas)
  }, [])
  return <MalhaDetalhe id="ventoinha" nome="ventoinha-pas-carenagem" criar={criar} cor="#303a42" />
}
